import { z } from 'zod';

import type {
    CreateTeamInputFE,
    TeamDetailFE,
    TeamFE,
    TeamMemberFE,
    TeamSummaryFE,
    UpdateTeamInputFE,
} from '../systemAdmin';
import type {
    MsFleetInfoTO,
    MsTeamCreateTO,
    MsTeamDetailTO,
    MsTeamMemberTO,
    MsTeamSummaryTO,
    MsTeamTO,
    MsTeamUpdateTO,
} from './msWireSpec';
import { emptyObjectToArray, makeSanitizeWire, safeOptional, safeRedactedOptional, toNamedRefs } from './restMapping';

/**
 * REST <-> domain mapping for teams, served by the management service's `TeamsApi`:
 * `POST /api/v1/teams` and `PUT /api/v1/teams/{id}` return `TeamTO` -> {@link teamFromRestV1};
 * `GET /api/v1/teams` returns `TeamSummaryTO[]` -> {@link teamsSummaryFromRestV1};
 * `GET /api/v1/teams/{id}` returns `TeamDetailTO` -> {@link teamDetailFromRestV1}.
 */

const label = (field: string) => `teamFromRestV1.${field}`;

/** `createdAt` / `updatedAt` — camelCase, which is why the shared `sanitizeWire` is not used. */
const TEAM_DATE_KEYS = new Set(['createdAt', 'updatedAt']);

const sanitizeTeamWire = makeSanitizeWire(TEAM_DATE_KEYS);

const WireTeamObjectSchema = z.object({
    id: z.string().min(1),
    name: safeOptional(z.string(), label('name')),
    createdAt: safeOptional(z.string(), label('createdAt')),
    updatedAt: safeOptional(z.string(), label('updatedAt')),
    users: safeOptional(z.array(z.string()), label('users')),
    fleets: safeOptional(z.array(z.string()), label('fleets')),
});

const WireTeamSummaryObjectSchema = z.object({
    id: z.string().min(1),
    name: safeOptional(z.string(), label('summary.name')),
    createdAt: safeOptional(z.string(), label('summary.createdAt')),
    updatedAt: safeOptional(z.string(), label('summary.updatedAt')),
    numberOfUsers: safeOptional(z.number(), label('summary.numberOfUsers')),
    numberOfFleets: safeOptional(z.number(), label('summary.numberOfFleets')),
});

/** A member's name degrades redacted: `safeOptional` would log the offending value. */
const WireTeamMemberObjectSchema = z.object({
    id: safeOptional(z.string(), label('detail.users[].id')),
    firstName: safeRedactedOptional(z.string(), label('detail.users[].firstName')),
    lastName: safeRedactedOptional(z.string(), label('detail.users[].lastName')),
});

const WireTeamFleetObjectSchema = z.object({
    id: safeOptional(z.string(), label('detail.fleets[].id')),
    name: safeOptional(z.string(), label('detail.fleets[].name')),
});

/**
 * `users` / `fleets` normalise `{}` to `[]` for the reason `emptyObjectToArray` documents. A
 * `users` collection that degrades does so redacted: it is a list of people, and a POPULATED
 * object — the deviation the normaliser exists for — would otherwise be logged in full.
 */
const WireTeamDetailObjectSchema = z.object({
    id: z.string().min(1),
    createdAt: safeOptional(z.string(), label('detail.createdAt')),
    updatedAt: safeOptional(z.string(), label('detail.updatedAt')),
    users: safeRedactedOptional(
        z.preprocess(emptyObjectToArray, z.array(WireTeamMemberObjectSchema)),
        label('detail.users')
    ),
    fleets: safeOptional(z.preprocess(emptyObjectToArray, z.array(WireTeamFleetObjectSchema)), label('detail.fleets')),
});

/** Wire schemas for the team payloads, exported for `mocks/tests/restContract.test.ts`. */
export const WireTeamSchema = z.preprocess(sanitizeTeamWire, WireTeamObjectSchema);
export const WireTeamSummarySchema = z.preprocess(sanitizeTeamWire, WireTeamSummaryObjectSchema);
export const WireTeamDetailSchema = z.preprocess(sanitizeTeamWire, WireTeamDetailObjectSchema);

type WireTeamObject = z.infer<typeof WireTeamObjectSchema>;
type WireTeamSummaryObject = z.infer<typeof WireTeamSummaryObjectSchema>;
type WireTeamDetailObject = z.infer<typeof WireTeamDetailObjectSchema>;
type WireTeamMemberObject = z.infer<typeof WireTeamMemberObjectSchema>;
type WireTeamFleetObject = z.infer<typeof WireTeamFleetObjectSchema>;

type KeysExistOn<TWire, TSpec> = Exclude<keyof TWire, keyof TSpec> extends never ? true : never;

export type TeamWireKeysExistOnSpec = {
    team: KeysExistOn<WireTeamObject, MsTeamTO>;
    teamCreate: KeysExistOn<MsTeamCreateTO, CreateTeamInputFE>;
    teamUpdate: KeysExistOn<MsTeamUpdateTO, UpdateTeamInputFE>;
    teamSummary: KeysExistOn<WireTeamSummaryObject, MsTeamSummaryTO>;
    teamDetail: KeysExistOn<WireTeamDetailObject, MsTeamDetailTO>;
    teamMember: KeysExistOn<WireTeamMemberObject, MsTeamMemberTO>;
    teamFleet: KeysExistOn<WireTeamFleetObject, MsFleetInfoTO>;
};

export const teamFromRestV1 = (raw: unknown): TeamFE => {
    const wire = WireTeamSchema.parse(raw);
    return {
        id: wire.id,
        name: wire.name ?? '',
        createdAt: wire.createdAt ?? '',
        updatedAt: wire.updatedAt ?? '',
        userIds: wire.users ?? [],
        fleetIds: wire.fleets ?? [],
    };
};

export const teamSummaryFromRestV1 = (raw: unknown): TeamSummaryFE => {
    const wire = WireTeamSummarySchema.parse(raw);
    return {
        id: wire.id,
        name: wire.name ?? '',
        createdAt: wire.createdAt ?? '',
        updatedAt: wire.updatedAt ?? '',
        numberOfUsers: wire.numberOfUsers ?? 0,
        numberOfFleets: wire.numberOfFleets ?? 0,
    };
};

export const teamsSummaryFromRestV1 = (raw: unknown): TeamSummaryFE[] => {
    if (!Array.isArray(raw)) {
        console.error(`Zod catch triggered (${label('summary.list')})`, raw);
        return [];
    }
    return raw.reduce<TeamSummaryFE[]>((acc, entry) => {
        try {
            acc.push(teamSummaryFromRestV1(entry));
        } catch (error) {
            console.error(`Zod catch triggered (${label('summary.list[]')})`, entry, error);
        }
        return acc;
    }, []);
};

/**
 * A member with no id would collide as a React key, so unidentifiable entries go. The dropped
 * entry is NOT logged, unlike `toNamedRefs`: a team member carries a first and last name.
 */
const membersFrom = (wire: WireTeamDetailObject['users']): TeamMemberFE[] =>
    (wire ?? []).reduce<TeamMemberFE[]>((acc, entry) => {
        if (!entry.id) {
            console.error(`Zod catch triggered (${label('detail.users[].id')})`);
            return acc;
        }
        acc.push({ id: entry.id, firstName: entry.firstName ?? '', lastName: entry.lastName ?? '' });
        return acc;
    }, []);

export const teamDetailFromRestV1 = (raw: unknown): TeamDetailFE => {
    const wire = WireTeamDetailSchema.parse(raw);
    return {
        id: wire.id,
        createdAt: wire.createdAt ?? '',
        updatedAt: wire.updatedAt ?? '',
        users: membersFrom(wire.users),
        fleets: toNamedRefs(wire.fleets, label('detail.fleets[].id')),
    };
};

export const toTeamCreateWire = (input: CreateTeamInputFE): MsTeamCreateTO => ({
    name: input.name,
    users: input.users,
    fleets: input.fleets,
});

export const toTeamUpdateWire = (input: UpdateTeamInputFE): MsTeamUpdateTO => ({
    name: input.name,
    users: input.users,
    fleets: input.fleets,
});
